import { useMemo, useRef } from 'react'
import {
  ConditionBuilder,
  type Condition,
  type FieldConfig,
  type OperatorConfig,
} from '@/components/common/ConditionBuilder'
import type { Channel, SuppressionRule } from '@super/shared-types'
import {
  SuppressionRuleField,
  SuppressionRuleOperator,
  MessageClassification,
  getAllowedOperatorsForField,
} from '@super/shared-types'
import {
  emailValidator,
  phoneValidator,
  countryCodeValidator,
  phoneFormatter,
  normalizePhoneToE164,
} from '@/lib/validators'

interface ChannelSettingsFormProps {
  channel: Channel
  rules: SuppressionRule[]
  onChange: (rules: SuppressionRule[]) => void
  onValidationChange?: (hasErrors: boolean) => void
}

// Field configurations per channel
const EMAIL_FIELDS: FieldConfig[] = [
  {
    value: SuppressionRuleField.EMAIL_ADDRESS,
    label: 'Email address',
    type: 'string',
    validator: emailValidator,
  },
  {
    value: SuppressionRuleField.EMAIL_DOMAIN,
    label: 'Email domain',
    type: 'string',
  },
  {
    value: SuppressionRuleField.EMAIL_SUBJECT,
    label: 'Email subject',
    type: 'string',
  },
  {
    value: SuppressionRuleField.EMAIL_HAS_ATTACHMENTS,
    label: 'Has attachments',
    type: 'boolean',
  },
  {
    value: SuppressionRuleField.MESSAGE_CONTENT,
    label: 'Message content',
    type: 'string',
  },
  {
    value: SuppressionRuleField.MESSAGE_CLASSIFICATION,
    label: 'Message classification',
    type: 'enum',
    enumValues: [
      { value: MessageClassification.SPAM, label: 'Spam' },
      { value: MessageClassification.SOLICITATION, label: 'Solicitation' },
    ],
  },
]

const SMS_FIELDS: FieldConfig[] = [
  {
    value: SuppressionRuleField.PHONE_NUMBER,
    label: 'Phone number',
    type: 'string',
    validator: phoneValidator,
    formatter: phoneFormatter,
    normalizer: normalizePhoneToE164,
  },
  {
    value: SuppressionRuleField.PHONE_COUNTRY_CODE,
    label: 'Country code',
    type: 'string',
    validator: countryCodeValidator,
  },
  {
    value: SuppressionRuleField.MESSAGE_CONTENT,
    label: 'Message content',
    type: 'string',
  },
  {
    value: SuppressionRuleField.MESSAGE_CLASSIFICATION,
    label: 'Message classification',
    type: 'enum',
    enumValues: [
      { value: MessageClassification.SPAM, label: 'Spam' },
      { value: MessageClassification.SOLICITATION, label: 'Solicitation' },
    ],
  },
]

const CALL_FIELDS: FieldConfig[] = [
  {
    value: SuppressionRuleField.PHONE_NUMBER,
    label: 'Phone number',
    type: 'string',
    validator: phoneValidator,
    formatter: phoneFormatter,
    normalizer: normalizePhoneToE164,
  },
  {
    value: SuppressionRuleField.PHONE_COUNTRY_CODE,
    label: 'Country code',
    type: 'string',
    validator: countryCodeValidator,
  },
]

// Operator configurations
const OPERATORS: OperatorConfig[] = [
  {
    value: SuppressionRuleOperator.EQUALS,
    label: 'is equal to',
    allowedForTypes: ['string', 'boolean'],
  },
  {
    value: SuppressionRuleOperator.NOT_EQUALS,
    label: 'is not equal to',
    allowedForTypes: ['string'],
  },
  {
    value: SuppressionRuleOperator.STARTS_WITH,
    label: 'starts with',
    allowedForTypes: ['string'],
  },
  {
    value: SuppressionRuleOperator.ENDS_WITH,
    label: 'ends with',
    allowedForTypes: ['string'],
  },
  {
    value: SuppressionRuleOperator.CONTAINS,
    label: 'contains',
    allowedForTypes: ['string'],
  },
  {
    value: SuppressionRuleOperator.IN,
    label: 'is one of',
    allowedForTypes: ['enum'],
  },
]

function getFieldsForChannel(channel: Channel): FieldConfig[] {
  switch (channel) {
    case 'EMAIL':
      return EMAIL_FIELDS
    case 'SMS':
      return SMS_FIELDS
    case 'CALL':
      return CALL_FIELDS
    default:
      return []
  }
}

export function ChannelSettingsForm({
  channel,
  rules,
  onChange,
  onValidationChange,
}: ChannelSettingsFormProps) {
  const fields = getFieldsForChannel(channel)

  // Maintain stable IDs for conditions by index position
  // Array of IDs corresponding to each rule by index
  const idsRef = useRef<string[]>([])

  // Convert SuppressionRule to Condition for ConditionBuilder
  // Assign stable IDs for validation tracking
  const conditions: Condition[] = useMemo(() => {
    // Ensure we have enough IDs for all rules
    while (idsRef.current.length < rules.length) {
      idsRef.current.push(crypto.randomUUID())
    }

    // Trim excess IDs if rules were removed
    if (idsRef.current.length > rules.length) {
      idsRef.current = idsRef.current.slice(0, rules.length)
    }

    return rules.map((rule, index) => ({
      id: idsRef.current[index],
      field: rule.field,
      operator: rule.operator,
      value: rule.value,
    }))
  }, [rules])

  // Convert Condition back to SuppressionRule
  const handleConditionsChange = (newConditions: Condition[]) => {
    const newRules: SuppressionRule[] = newConditions.map(condition => ({
      field: condition.field as SuppressionRuleField,
      operator: condition.operator as SuppressionRuleOperator,
      value: condition.value,
    }))
    onChange(newRules)
  }

  // Get allowed operators for a specific field
  const getOperatorsForField = (fieldValue: string): OperatorConfig[] => {
    const allowedOperators = getAllowedOperatorsForField(
      fieldValue as SuppressionRuleField,
    )
    return OPERATORS.filter(op =>
      allowedOperators.includes(op.value as SuppressionRuleOperator),
    )
  }

  // Handle validation state changes
  const handleValidationChange = (hasErrors: boolean) => {
    onValidationChange?.(hasErrors)
  }

  return (
    <div className="space-y-4">
      <ConditionBuilder
        fields={fields}
        operators={OPERATORS}
        conditions={conditions}
        onChange={handleConditionsChange}
        addButtonLabel="Add Suppression Rule"
        getOperatorsForField={getOperatorsForField}
        onValidationChange={handleValidationChange}
      />
    </div>
  )
}
